import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { addSpecialty } from "../../Adapters/ApiAdapter";
import { baseUrl } from "../../variables/Variables";
import QuillWithHexColor from "../../components/QuillWithHexColor";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import CKEditorField from "../../components/CKEditorField";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }], // ✅ color support
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"]
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "link",
  "image",
];


const AddSpecialties = () => {
  const [fileUploading, setFileUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    overview: "",
    disease: "",
    treatment: "",
    technology: "",
    procedure: "",
    video_link: "",
    status: "",
    img_path: "",
    banner_img: "",
    seo_title: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    custom_scripts: "",
    og_title: "",
    og_description: "",
    og_type: "",
    og_url: "",
    og_image: "",
    og_locale: "",
    og_site_name: "",
    og_image_width: "",
    og_image_height: "",
    og_image_type: "",
    article_modified_time: "",
    canonical_link: "",
    twitter_card: "",
    twitter_title: "",
    twitter_description: "",
    twitter_image: "",
    twitter_site: "",
  });
  const [imgFile, setImgFile] = useState(null); // Added for image upload
  const [bannerImgFile, setBannerImgFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ogImgFile, setOgImgFile] = useState(null);
  const [twitterImgFile, setTwitterImgFile] = useState(null);
  const quillRef = useRef(null);
  const navigate = useNavigate();


  const applyCustomColor = (color) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      editor.format("color", color);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      let updatedForm = { ...prev, [name]: value };

      if (name === "name") {
        updatedForm.slug = generateSlug(value);
      }

      return updatedForm;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImgFile(file);
    }
  };
  const handleBannerFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerImgFile(file);
    }
  };
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/&/g, " ")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const specialtyData = new FormData();

    specialtyData.append("name", formData.name);
    specialtyData.append("slug", formData.slug);
    specialtyData.append("description", formData.description);
    specialtyData.append("overview", formData.overview);
    specialtyData.append("disease", formData.disease);
    specialtyData.append("treatment", formData.treatment);
    specialtyData.append("technology", formData.technology);
    specialtyData.append("procedure", formData.procedure || "");
    specialtyData.append("status", formData.status || "");
    specialtyData.append("video_link", formData.video_link || "");

    specialtyData.append("seo_title", formData.meta_title || "");
    specialtyData.append("meta_title", formData.meta_title || "");
    specialtyData.append("meta_description", formData.meta_description || "");
    specialtyData.append("meta_keywords", formData.meta_keywords || "");
    specialtyData.append("custom_scripts", formData.custom_scripts || "");
    specialtyData.append("og_title", formData.og_title || "");
    specialtyData.append("og_description", formData.og_description || "");
    specialtyData.append("og_type", formData.og_type || "");
    specialtyData.append("og_url", formData.og_url || "");
    specialtyData.append("og_locale", formData.og_locale || "");
    specialtyData.append("og_site_name", formData.og_site_name || "");
    specialtyData.append("og_image_width", formData.og_image_width || "");
    specialtyData.append("og_image_height", formData.og_image_height || "");
    specialtyData.append("og_image_type", formData.og_image_type || "");
    specialtyData.append(
      "article_modified_time",
      formData.article_modified_time || ""
    );
    specialtyData.append("canonical_link", formData.canonical_link || "");
    specialtyData.append("twitter_card", formData.twitter_card || "");
    specialtyData.append("twitter_title", formData.twitter_title || "");
    specialtyData.append(
      "twitter_description",
      formData.twitter_description || ""
    );
    specialtyData.append("twitter_site", formData.twitter_site || "");
    specialtyData.append("twitter_site", formData.twitter_site || "");

    // if (imgFile) {
    //   specialtyData.append("img_file", imgFile);
    // }
    // if (bannerImgFile) {
    //   specialtyData.append("banner_img", bannerImgFile);
    // }
    // if (ogImgFile) specialtyData.append("og_image", ogImgFile);

    // if (twitterImgFile) specialtyData.append("twitter_image", twitterImgFile);
    if (imgFile) {
      const timestamp = Date.now();
      const renamed = new File([imgFile], `${timestamp}_${imgFile.name}`, { type: imgFile.type });
      specialtyData.append("img_file", renamed);
    }

    if (bannerImgFile) {
      const timestamp = Date.now();
      const renamed = new File([bannerImgFile], `${timestamp}_${bannerImgFile.name}`, { type: bannerImgFile.type });
      specialtyData.append("banner_img", renamed);
    }

    if (ogImgFile) {
      const timestamp = Date.now();
      const renamed = new File([ogImgFile], `${timestamp}_${ogImgFile.name}`, { type: ogImgFile.type });
      specialtyData.append("og_image", renamed);
    }

    if (twitterImgFile) {
      const timestamp = Date.now();
      const renamed = new File([twitterImgFile], `${timestamp}_${twitterImgFile.name}`, { type: twitterImgFile.type });
      specialtyData.append("twitter_image", renamed);
    }

    try {
      console.log("Specialty Data:", [...specialtyData]); // Log the FormData object
      const response = await addSpecialty(specialtyData);
      console.log("Response:", response); // Log the API response
      if (response && response.status === "true") {
        toast.success("Specialty added successfully!");
        setFormData({
          name: "",
          slug: "",
          description: "",
          overview: "",
          disease: "",
          treatment: "",
          technology: "",
          status: "",
          img_path: "",
          banner_img: "",
          procedure: "",
          seo_title: "",
          meta_title: "",
          meta_description: "",
          meta_keywords: "",
          custom_scripts: "",
          og_title: "",
          og_description: "",
          og_type: "",
          og_url: "",
          og_image: "",
          og_locale: "",
          og_site_name: "",
          og_image_width: "",
          og_image_height: "",
          og_image_type: "",
          article_modified_time: "",
          canonical_link: "",
          twitter_card: "",
          twitter_title: "",
          twitter_description: "",
          twitter_image: "",
          twitter_site: "",
        });
        setImgFile(null); // Reset the image file
        setBannerImgFile(null);
        setOgImgFile(null);
        setTwitterImgFile(null);
        setTimeout(() => {
          navigate("/list-specialties");
        }, 1500);

      } else {
        const errorMessage = response?.error || response?.message || "Failed to add specialty..";
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error("An error occurred while adding the specialty.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Add Specialty
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          margin="normal"
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
        />
        <TextField
          fullWidth
          margin="normal"
          label="Slug"
          name="slug"
          value={formData.slug}
          onChange={handleInputChange}
          required
        />
        <Typography variant="body1">Description *</Typography>
        <ReactQuill
          theme="snow"
          value={formData.description}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, description: value }));
          }}
          style={{ height: "200px", marginBottom: "50px" }}
        /> */}
        <CKEditorField
            value={formData.description}
            onChange={(data) => setFormData((prev) => ({ ...prev, description: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=speciality`}
          />

        {/* <Typography variant="body1">Overview</Typography>
        <ReactQuill
          theme="snow"
          value={formData.overview}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, overview: value }));
          }}
          modules={quillModules}
          formats={quillFormats}
          style={{ height: "200px", marginBottom: "50px" }}
        /> */}
        <Typography variant="body1">Overview *</Typography>
        <QuillWithHexColor
          id="overview"
          value={formData.overview}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, overview: value }))
          }
        /> */}
        <CKEditorField
            value={formData.overview}
            onChange={(data) => setFormData((prev) => ({ ...prev, overview: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=speciality`}
          />

        {/* <Typography variant="body1">Disease</Typography>
        <input
          type="color"
          onChange={(e) => applyCustomColor(e.target.value)}
          title="Pick text color"
          style={{ marginBottom: "10px", marginRight: "10px" }}
        />
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={formData.disease}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, disease: value }));
          }}
          modules={quillModules}
          formats={quillFormats}
          style={{ height: "200px", marginBottom: "50px" }}
        /> */}
        <Typography variant="body1">Disease</Typography>
        {/* <QuillWithHexColor
          id="disease"
          value={formData.disease}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, disease: value }))
          }
        /> */}
        <CKEditorField
            value={formData.disease}
            onChange={(data) => setFormData((prev) => ({ ...prev, disease: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=speciality`}
          />
        {/* <Typography>Treatment</Typography>
        <input
          type="color"
          onChange={(e) => applyCustomColor(e.target.value)}
          title="Pick text color"
          style={{ marginBottom: "10px", marginRight: "10px" }}
        />
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={formData.treatment}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, treatment: value }));
          }}
          modules={quillModules}
          formats={quillFormats}
          style={{ height: "200px", marginBottom: "50px" }}
        /> */}
        <Typography variant="body1">Treatment</Typography>
        {/* <QuillWithHexColor
          id="treatment"
          value={formData.treatment}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, treatment: value }))
          }
        /> */}
        <CKEditorField
            value={formData.treatment}
            onChange={(data) => setFormData((prev) => ({ ...prev, treatment: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=speciality`}
          />

        {/* <Typography>Technology</Typography>
        <input
          type="color"
          onChange={(e) => applyCustomColor(e.target.value)}
          title="Pick text color"
          style={{ marginBottom: "10px", marginRight: "10px" }}
        />
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={formData.technology}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, technology: value }));
          }}
          modules={quillModules}
          formats={quillFormats}
          style={{ height: "200px", marginBottom: "50px" }}
        /> */}
        <Typography variant="body1">Technology</Typography>
        {/* <QuillWithHexColor
          id="technology"
          value={formData.technology}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, technology: value }))
          }
        /> */}
        <CKEditorField
            value={formData.technology}
            onChange={(data) => setFormData((prev) => ({ ...prev, technology: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=speciality`}
          />

        {/* <Typography>Procedure</Typography>
        <input
          type="color"
          onChange={(e) => applyCustomColor(e.target.value)}
          title="Pick text color"
          style={{ marginBottom: "10px", marginRight: "10px" }}
        />
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={formData.procedure}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, procedure: value }));
          }}
          modules={quillModules}
          formats={quillFormats}
          style={{ height: "200px", marginBottom: "50px" }}
        /> */}
        <Typography variant="body1">Procedure</Typography>
        {/* <QuillWithHexColor
          id="procedure"
          value={formData.procedure}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, procedure: value }))
          }
        /> */}
        <CKEditorField
            value={formData.procedure}
            onChange={(data) => setFormData((prev) => ({ ...prev, procedure: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=speciality`}
          />

        <TextField
          fullWidth
          margin="normal"
          label="Video Link"
          name="video_link"
          value={formData.video_link}
          onChange={handleInputChange}
        />

        {/* <TextField
          fullWidth
          margin="normal"
          label="status"
          name="status"
          value={formData.status}
          onChange={handleInputChange}
        /> */}

	  <TextField
          fullWidth
          margin="normal"
          label="SEO Title"
          name="seo_title"
          value={formData.seo_title}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="SEO Title"
          name="seo_title"
          value={formData.seo_title}
          onChange={handleInputChange}
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="Meta Title"
          name="meta_title"
          value={formData.meta_title}
          onChange={handleInputChange}
        />

        <Typography variant="body1">Meta Description</Typography>
        {/* <ReactQuill
          theme="snow"
          value={formData.meta_description}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, meta_description: value }));
          }}
          style={{ height: "200px", marginBottom: "50px" }}
        /> */}
        <CKEditorField
            value={formData.meta_description}
            onChange={(data) => setFormData((prev) => ({ ...prev, meta_description: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=speciality`}
          />

        <TextField
          fullWidth
          margin="normal"
          label="Meta Keywords"
          name="meta_keywords"
          value={formData.meta_keywords}
          onChange={handleInputChange}
        />

        <Typography variant="body1">Custom Scripts</Typography>
        {/* <ReactQuill
          theme="snow"
          value={formData.custom_scripts}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, custom_scripts: value }));
          }}
          style={{ height: "200px", marginBottom: "50px" }}
        /> */}
        <CKEditorField
            value={formData.custom_scripts}
            onChange={(data) => setFormData((prev) => ({ ...prev, custom_scripts: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=speciality`}
          />

        <TextField
          fullWidth
          margin="normal"
          label="OG Title"
          name="og_title"
          value={formData.og_title}
          onChange={handleInputChange}
        />

        <Typography variant="body1">OG Description</Typography>
        {/* <ReactQuill
          theme="snow"
          value={formData.og_description}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, og_description: value }));
          }}
          style={{ height: "200px", marginBottom: "50px" }}
        /> */}
        <CKEditorField
            value={formData.og_description}
            onChange={(data) => setFormData((prev) => ({ ...prev, og_description: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=speciality`}
          />

        <TextField
          fullWidth
          margin="normal"
          label="OG Type"
          name="og_type"
          value={formData.og_type}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="OG URL"
          name="og_url"
          value={formData.og_url}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="OG Locale"
          name="og_locale"
          value={formData.og_locale}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="OG Site Name"
          name="og_site_name"
          value={formData.og_site_name}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="OG Image Width"
          name="og_image_width"
          value={formData.og_image_width}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="OG Image Height"
          name="og_image_height"
          value={formData.og_image_height}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="OG Image Type"
          name="og_image_type"
          value={formData.og_image_type}
          onChange={handleInputChange}
        />
        <Typography variant="body1">Article Modified Time</Typography>
        <TextField
          fullWidth
          // margin="normal"
          type="datetime-local"
          // label="Article Modified Time"
          name="article_modified_time"
          value={formData.article_modified_time}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Canonical Link"
          name="canonical_link"
          value={formData.canonical_link}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Twitter Card"
          name="twitter_card"
          value={formData.twitter_card}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Twitter Title"
          name="twitter_title"
          value={formData.twitter_title}
          onChange={handleInputChange}
        />

        <Typography variant="body1">Twitter Description</Typography>
        {/* <ReactQuill
          theme="snow"
          value={formData.twitter_description}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, twitter_description: value }));
          }}
          style={{ height: "200px", marginBottom: "50px" }}
        /> */}
        <CKEditorField
            value={formData.twitter_description}
            onChange={(data) => setFormData((prev) => ({ ...prev, twitter_description: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=speciality`}
          />
        <TextField
          fullWidth
          margin="normal"
          label="Twitter Site"
          name="twitter_site"
          value={formData.twitter_site}
          onChange={handleInputChange}
        />

        <Button
          variant="contained"
          component="label"
          style={{ marginTop: "20px" }}
        >
          Upload Speciality Image
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileChange}
          />
        </Button>
        {imgFile && (
          <Typography
            variant="body2"
            style={{ marginTop: "10px", fontStyle: "italic" }}
          >
            Selected File: {imgFile.name}
          </Typography>
        )}
        <br /><Typography variant="caption" color="textSecondary" style={{ color: 'red' }}>
          Specialty Image should be 60x60px
        </Typography>


        <Box mt={3}>
          <Button
            variant="contained"
            component="label"
            style={{ marginTop: "10px" }}
          >
            Upload Banner Image
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleBannerFileChange}
            />
          </Button>
          {bannerImgFile && (
            <Typography
              variant="body2"
              style={{ marginTop: "10px", fontStyle: "italic" }}
            >
              Selected File: {bannerImgFile.name}
            </Typography>
          )}
          <br /><Typography variant="caption" color="textSecondary" style={{ color: 'red' }}>
            Banner Image should be 1380x260px
          </Typography>
        </Box>

        <Box mt={3}>
          <Button
            variant="contained"
            component="label"
            style={{ marginTop: "20px" }}
          >
            Upload OG Image
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => setOgImgFile(e.target.files[0])}
            />
          </Button>
          {ogImgFile && (
            <Typography
              variant="body2"
              style={{ marginTop: "10px", fontStyle: "italic" }}
            >
              Selected File: {ogImgFile.name}
            </Typography>
          )}
        </Box>

        <Box mt={3}>
          <Button
            variant="contained"
            component="label"
            style={{ marginTop: "10px" }}
          >
            Upload Twitter Image
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => setTwitterImgFile(e.target.files[0])}
            />
          </Button>
          {twitterImgFile && (
            <Typography
              variant="body2"
              style={{ marginTop: "10px", fontStyle: "italic" }}
            >
              Selected File: {twitterImgFile.name}
            </Typography>
          )}
        </Box>

        <Box mt={3}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            style={{ background: '#0ca854' }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Add Specialty"}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default AddSpecialties;
